import uniqBy from 'lodash/uniqBy';

import isNotNullish from '@watershed/shared-util/src/isNotNullish';

import type {
  CalculationModel,
  UnboundVariable,
} from '../../calculationModel/calculationModelSchema';
import { GLOBAL_SCOPE_PREFIX } from '../../calculationModel/constants';
import pluck from '@watershed/shared-util/src/pluck';
import {
  ActivityDataColumn,
  BatActivityDataColumn,
  CustomActivityDataColumn,
} from './activityDataColumns';
import type { CalculationModelMatcherBinding } from './entities';

// - unbound : an input variable that exists on the calculation model
// - bound : a model scoped variable which might be customizable
// - custom-activity-data : a user defined custom activity data column that can be used for customization
// - optional-activity-data : an optional BART activity data column that can be used for customization
export type CalculationMethodVariableKind =
  | 'unbound'
  | 'bound'
  | 'custom-activity-data'
  | 'optional-activity-data';

export interface CalculationMethodVariable {
  name: string;
  displayName: string;
  type: string;
  unit: string | null;
  description: string | null;
  expressionColumnText: string;
  kind: CalculationMethodVariableKind;
}

export interface CalculationMethodVariableGroup {
  displayName: string;
  variables: Array<CalculationMethodVariable>;
}

/**
 * A map of parameter names to their parameter expressions.
 * Used for efficient lookup of parameter expressions by name.
 */
export type ParameterExpressions = Record<string, string>;

/**
 * A simple function, but we pull this out so we can do it the same way
 * everywhere we need to introspect/qualify a variable name for a given node (or
 * global prefix).
 */
export function qualifiedVariableForNode({
  nodeName,
  variableName,
}: {
  nodeName: string;
  variableName: string;
}): string {
  return `${nodeName}__${variableName}`;
}

/**
 * Creates a bound calculation variable from a scoped name, a variable, and a
 * parameter expression.
 */
const createBoundCalculationVariable = (
  scopedName: string,
  variable: UnboundVariable,
  parameterExpression: string
): CalculationMethodVariable => {
  return {
    name: scopedName,
    displayName: variable.displayName ?? variable.name,
    type: variable.type,
    unit: variable.unit,
    description: variable.description,
    expressionColumnText: parameterExpression,
    kind: 'bound',
  };
};

/**
 * Creates an unbound calculation variable from a name and a variable.
 * If a parameter expression is provided, uses that expression; otherwise
 * indicates no BART matcher was found.
 */
const createUnboundCalculationVariable = (
  variable: UnboundVariable,
  parameterExpression: string
): CalculationMethodVariable => {
  return {
    name: variable.name,
    displayName: variable.displayName ?? variable.name,
    type: variable.type,
    unit: variable.unit,
    description: variable.description,
    expressionColumnText: parameterExpression,
    kind: 'unbound',
  };
};

/**
 * Creates a custom activity data calculation variable from a custom column
 */
const createCustomActivityDataCalculationVariable = (
  variable: CustomActivityDataColumn
): CalculationMethodVariable => {
  return {
    name: variable.fieldName,
    displayName: variable.displayName ?? variable.fieldName,
    type: variable.type,
    unit: null,
    description: variable.description ?? null,
    expressionColumnText: variable.fieldName,
    kind: 'custom-activity-data',
  };
};

/**
 * Creates an optional activity data calculation variable from a BART activity
 * data column.
 */
const createOptionalActivityDataCalculationVariable = (
  variable: BatActivityDataColumn
): CalculationMethodVariable => {
  return {
    name: variable.fieldName,
    displayName: variable.displayName ?? variable.fieldName,
    type: variable.type,
    unit: null,
    description: variable.description ?? null,
    expressionColumnText: variable.fieldName,
    kind: 'optional-activity-data',
  };
};

/**
 * Converts an activity data column to a calculation method variable.
 */
const convertActivityDataColumnToCalculationMethodVariable = (
  variable: ActivityDataColumn
): CalculationMethodVariable => {
  if (variable.isCustom) {
    return createCustomActivityDataCalculationVariable(
      variable as CustomActivityDataColumn
    );
  }

  return createOptionalActivityDataCalculationVariable(
    variable as BatActivityDataColumn
  );
};

/**
 * Gets optional activity variables from the calculation methods.
 * Converts the activity data columns to calculation method variables.
 */
export const getOptionalActivityVariables = ({
  optionalActivityVariables,
}: {
  optionalActivityVariables: Array<ActivityDataColumn>;
}): Array<CalculationMethodVariable> => {
  return uniqBy(
    optionalActivityVariables.map(
      convertActivityDataColumnToCalculationMethodVariable
    ),
    pluck('name')
  );
};

/**
 * Gets global reference data variables from the calculation model by binding
 * the global variables to the parameter expressions.
 *
 * Filters out variables that do not have a parameter expression.
 */
const getGlobalRefDataVariables = ({
  calculationModel,
  parameterExpressions,
}: {
  calculationModel: CalculationModel;
  parameterExpressions: ParameterExpressions;
}): Array<CalculationMethodVariable> => {
  return Object.entries(calculationModel.globalVariables)
    .map(([name, globalVariable]) => {
      const scopedName = qualifiedVariableForNode({
        nodeName: GLOBAL_SCOPE_PREFIX,
        variableName: name,
      });
      const expression = parameterExpressions[scopedName];
      if (!expression) {
        return null;
      }

      return createBoundCalculationVariable(
        scopedName,
        globalVariable,
        expression
      );
    })
    .filter(isNotNullish);
};

/**
 * Gets all local variables for a calculation model by binding the local
 * variables to the parameter expressions.
 *
 * If a variable does not have a parameter expression, it will be set to an
 * empty string.
 */
const getAllLocalVariables = ({
  calculationModel,
  parameterExpressions,
}: {
  calculationModel: CalculationModel;
  parameterExpressions: ParameterExpressions;
}): Array<CalculationMethodVariable> => {
  return calculationModel.nodes.flatMap((node) => {
    return Object.entries(node.variables).flatMap(
      ([variableName, nodeVariable]) => {
        const scopedName = qualifiedVariableForNode({
          nodeName: node.name,
          variableName,
        });
        const expression = parameterExpressions[scopedName];

        return createBoundCalculationVariable(
          scopedName,
          nodeVariable,
          expression ?? ''
        );
      }
    );
  });
};

/**
 *
 * Gets all input variables for a top-level calculation model by binding the
 * input variables to the matcher bindings.
 *
 * If an input variable does not have a matcher binding,  it will be set to 'Set
 * by importing method'. This is expected for imported models which are reused
 * in other models.
 */
const getInputVariablesForTopLevelModel = ({
  calculationModel,
  matcherBindings,
}: {
  calculationModel: CalculationModel;
  matcherBindings: Array<CalculationModelMatcherBinding>;
}): Array<CalculationMethodVariable> => {
  return Object.entries(calculationModel.inputVariables)
    .map(([_, inputVariable]) => {
      const matcherBinding = matcherBindings.find(
        (binding) => binding.name === inputVariable.name
      );

      const expression =
        matcherBinding?.expression ?? 'Set by importing method';
      return createUnboundCalculationVariable(inputVariable, expression);
    })
    .filter(isNotNullish);
};

/**
 * Gets all input variables for an imported descendant by binding the input
 * variables to the import param bindings.
 *
 * If an input variable does not have an import param binding, it will be set to
 * 'Set by importing method'.
 */
const getInputVariablesForImportedDescendant = ({
  calculationModel,
  importParamBindings,
}: {
  calculationModel: CalculationModel;
  importParamBindings: ParameterExpressions;
}): Array<CalculationMethodVariable> => {
  return Object.entries(calculationModel.inputVariables).map(
    ([_, inputVariable]) => {
      const importParamBinding = importParamBindings[inputVariable.name];
      const expression = importParamBinding ?? 'Set by importing method';
      return createUnboundCalculationVariable(inputVariable, expression);
    }
  );
};

/**
 * Gets all input, global ref data, optional activity, and local variables for a
 * top-level calculation model.
 *
 * Local variables are only included if they are actually bound (used).
 */
export const getAllVariablesForTopLevelCalculationModel = (
  {
    calculationModel,
    parameterExpressions,
    matcherBindings,
  }: {
    calculationModel: CalculationModel;
    parameterExpressions: ParameterExpressions;
    matcherBindings: Array<CalculationModelMatcherBinding>;
  },
  deps: {
    getAllLocalVariables: typeof getAllLocalVariables;
    getInputVariablesForTopLevelModel: typeof getInputVariablesForTopLevelModel;
    getGlobalRefDataVariables: typeof getGlobalRefDataVariables;
  } = {
    getAllLocalVariables,
    getInputVariablesForTopLevelModel,
    getGlobalRefDataVariables,
  }
): {
  localVariables: Array<CalculationMethodVariable>;
  inputVariables: Array<CalculationMethodVariable>;
  globalRefDataVariables: Array<CalculationMethodVariable>;
} => {
  // For top-level models, only include locals that are actually bound (used).
  const allLocalVariables = deps.getAllLocalVariables({
    calculationModel,
    parameterExpressions,
  });
  const localVariables = allLocalVariables.filter(
    (v) => v.expressionColumnText !== ''
  );

  return {
    localVariables,
    inputVariables: deps.getInputVariablesForTopLevelModel({
      calculationModel,
      matcherBindings,
    }),
    globalRefDataVariables: deps.getGlobalRefDataVariables({
      calculationModel,
      parameterExpressions,
    }),
  };
};

/**
 * Gets all input, global ref data, and local variables for an imported
 * descendant.
 */
export const getAllVariablesForImportedDescendant = (
  {
    calculationModel,
    parameterExpressions,
    importParamBindings,
  }: {
    calculationModel: CalculationModel;
    parameterExpressions: ParameterExpressions;
    importParamBindings: Record<string, string>;
  },
  deps: {
    getAllLocalVariables: typeof getAllLocalVariables;
    getInputVariablesForImportedDescendant: typeof getInputVariablesForImportedDescendant;
    getGlobalRefDataVariables: typeof getGlobalRefDataVariables;
  } = {
    getAllLocalVariables,
    getInputVariablesForImportedDescendant,
    getGlobalRefDataVariables,
  }
): {
  localVariables: Array<CalculationMethodVariable>;
  inputVariables: Array<CalculationMethodVariable>;
  globalRefDataVariables: Array<CalculationMethodVariable>;
} => {
  return {
    localVariables: deps.getAllLocalVariables({
      calculationModel,
      parameterExpressions,
    }),
    inputVariables: deps.getInputVariablesForImportedDescendant({
      calculationModel,
      importParamBindings,
    }),
    globalRefDataVariables: deps.getGlobalRefDataVariables({
      calculationModel,
      parameterExpressions,
    }),
  };
};

export const exportedForTesting = {
  createBoundCalculationVariable,
  createUnboundCalculationVariable,
  createCustomActivityDataCalculationVariable,
  createOptionalActivityDataCalculationVariable,
  convertActivityDataColumnToCalculationMethodVariable,
  getOptionalActivityVariables,
  getGlobalRefDataVariables,
  getAllLocalVariables,
  getInputVariablesForTopLevelModel,
  getInputVariablesForImportedDescendant,
};
